import React, { useEffect, useState } from 'react'
import { ThemeProvider } from '@emotion/react'
import { useAppDispatch, useAppSelector } from './redux/hooks'
import { Outlet, useParams } from 'react-router'
import { fusionScreenTheme } from '@fusion-advantage/formrender'
import { PREVIEW_ENCOUNTER_GUID, RootRouteContentType, SyncResultStatus, WorkItemAction } from '@fusion-advantage/formlib'
import { setAppContent, setPreviewBranchInfo, startPreview } from './redux/appSlice'
import { dashboardDataReloadFromLdb } from './redux/dashboardDataSlice'
import { selectPreviewBranchInfo } from './dal2/selectPreviewBranchInfo'
import { appError } from './redux/errorSlice'
import { appSync } from './redux/syncthunk/syncThunk'
import { SyncType } from './redux/syncthunk/SyncConfig'
import { LxAppIntro } from './stories/content/dashboard/LxAppIntro'

export const PreviewRouteParams = {
	branchGuid: 'branchGuid',
} as const

const GUID_PATTERN = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/

enum PreviewStatus {
	Loading = 'loading',
	NeedsLogin = 'needsLogin',
	Failed = 'failed',
	Ready = 'ready',
}

export const PreviewRoute = React.memo(props => {
	const booted = useAppSelector(state => state.app.booted)
	const content = useAppSelector(state => state.app.content)
	const dispatch = useAppDispatch()
	const routeParams = useParams()
	const branchGuid = routeParams[PreviewRouteParams.branchGuid]?.toUpperCase()
	const isValidGuid = Boolean(branchGuid && GUID_PATTERN.test(branchGuid))
	const [status, setStatus] = useState(PreviewStatus.Loading)

	// enter preview mode before anything else can run: this (child) effect
	// fires before AppBooter's boot, so no write or background sync is ever
	// possible outside of preview mode on this route
	useEffect(() => {
		if(branchGuid && isValidGuid) {
			dispatch(startPreview(branchGuid))
		}
	}, [branchGuid, isValidGuid])

	useEffect(() => {
		(async function() {
			if(!branchGuid || !isValidGuid || !booted) {
				return
			}
			const rSync = await dispatch(appSync({
				type: SyncType.Preview,
				adhocBranchGuid: branchGuid,
			}))
			if(rSync.isErr()) {
				// no session cookie: the login screen returns here afterward
				setStatus(rSync.error.status === SyncResultStatus.NoAuth
					? PreviewStatus.NeedsLogin
					: PreviewStatus.Failed)
				return
			}
			// any logged-in user may preview; this loads their identity for the form
			await dispatch(dashboardDataReloadFromLdb())
			// designator plus the branch's live renderer (null = built-in)
			const rBranchInfo = await selectPreviewBranchInfo(branchGuid)
			if(rBranchInfo.isErr()) {
				dispatch(appError({err: rBranchInfo.error}))
				setStatus(PreviewStatus.Failed)
				return
			}
			dispatch(setPreviewBranchInfo(rBranchInfo.value))
			await dispatch(setAppContent({
				type: RootRouteContentType.Form,
				encounterGuid: PREVIEW_ENCOUNTER_GUID,
				action: WorkItemAction.ContinueForm,
			}))
			setStatus(PreviewStatus.Ready)
		})()
	}, [branchGuid, isValidGuid, booted])

	if(!branchGuid || !isValidGuid) {
		return <h2>Preview URL does not contain a valid branch GUID</h2>
	}

	switch(status) {
		case PreviewStatus.NeedsLogin:
			return <LxAppIntro />
		case PreviewStatus.Failed:
			return <h2>Preview failed to load; see the console for details</h2>
	}

	if(content.type !== RootRouteContentType.Form) {
		if(status === PreviewStatus.Ready) {
			return <>
				<h2>Preview ended</h2>
				<p>The form is no longer open. Reload to preview the branch again.</p>
			</>
		}
		return <h2>Loading branch {branchGuid}...</h2>
	}

	return (
		<ThemeProvider theme={fusionScreenTheme}>
			<Outlet />
		</ThemeProvider>
	)
})
